import React, { useEffect, useState } from 'react';
import { Compass, Check, Circle, Waves, TrendingUp, Pause, ChevronDown } from 'lucide-react';
import advisor from '../../lib/advisor';

/**
 * DecisionQualityPanel — Release 0.7
 *
 * Sección "¿Cómo va tu decisión?" — evalúa el proceso, nunca al usuario
 * ni a la propiedad. Sin porcentajes, sin score. Tono empático.
 *
 * Props:
 *   - quality: DecisionQuality del backend
 */

const READINESS_META = {
  exploring: {
    label: 'Explorando',
    icon: Compass,
    tone: 'bg-blue-50 border-blue-200 text-blue-800',
  },
  focusing: {
    label: 'Enfocando',
    icon: Waves,
    tone: 'bg-amber-50 border-amber-200 text-amber-800',
  },
  comparing: {
    label: 'Comparando',
    icon: TrendingUp,
    tone: 'bg-purple-50 border-purple-200 text-purple-800',
  },
  ready: {
    label: 'Listo para decidir',
    icon: Check,
    tone: 'bg-habita-mint border-habita/40 text-habita-dark',
  },
};

const MOMENTUM_META = {
  growing: { label: 'Avanzando', icon: TrendingUp, tone: 'text-emerald-700' },
  stable: { label: 'Ritmo constante', icon: Waves, tone: 'text-gray-600' },
  stalled: { label: 'Pausado', icon: Pause, tone: 'text-amber-700' },
};


export default function DecisionQualityPanel({ quality }) {
  const [openedInsights, setOpenedInsights] = useState({});

  if (!quality) return null;

  const readinessMeta = READINESS_META[quality.readiness] || READINESS_META.exploring;
  const momentumMeta = MOMENTUM_META[quality.momentum] || MOMENTUM_META.stable;
  const RIcon = readinessMeta.icon;
  const MIcon = momentumMeta.icon;

  const defined = quality.completeness.filter((a) => a.is_defined);
  const openAspects = quality.completeness.filter((a) => !a.is_defined);

  const toggleInsight = (insight) => {
    setOpenedInsights((prev) => {
      const next = { ...prev, [insight.id]: !prev[insight.id] };
      if (!prev[insight.id]) {
        // primera apertura → registrar métrica
        advisor.recordInsightOpened(insight.id).catch(() => {});
      }
      return next;
    });
  };

  return (
    <div
      className="rounded-3xl border border-gray-200 bg-white p-5 sm:p-7"
      data-testid="quality-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-full bg-habita text-white grid place-items-center">
          <Compass className="w-4 h-4" />
        </div>
        <p className="text-[11px] uppercase tracking-widest text-habita-dark/70 font-semibold">
          Habita Advisor · ¿Cómo va tu decisión?
        </p>
      </div>

      {/* Estado del proceso */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="quality-readiness-row">
        <span
          data-testid="quality-readiness-chip"
          className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-sm font-medium ${readinessMeta.tone}`}
        >
          <RIcon className="w-3.5 h-3.5" />
          {readinessMeta.label}
        </span>
        <span
          data-testid="quality-momentum-chip"
          className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-gray-50 border border-gray-200 text-sm ${momentumMeta.tone}`}
        >
          <MIcon className="w-3.5 h-3.5" />
          {momentumMeta.label}
        </span>
      </div>

      {/* Mensaje humano — sin porcentajes */}
      <p
        className="font-serif text-lg text-habita-dark leading-relaxed mb-2"
        data-testid="quality-confidence-message"
      >
        {quality.confidence_message}
      </p>
      {quality.momentum_reason && (
        <p className="text-xs text-gray-500 mb-6">
          {quality.momentum_reason}
        </p>
      )}

      {/* Aspectos — reflexión, no checklist */}
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-6" data-testid="quality-completeness">
        {[...defined, ...openAspects].map((a) => (
          <div key={a.key} className="flex items-start gap-2 py-1">
            {a.is_defined ? (
              <Check className="w-4 h-4 mt-0.5 text-habita shrink-0" data-testid={`quality-aspect-${a.key}-defined`} />
            ) : (
              <Circle className="w-4 h-4 mt-0.5 text-gray-300 shrink-0" data-testid={`quality-aspect-${a.key}-open`} />
            )}
            <div>
              <p className={`text-sm ${a.is_defined ? 'text-habita-dark' : 'text-gray-700'}`}>
                <span className="font-medium">{a.label}.</span>{' '}
                <span className={a.is_defined ? 'text-gray-600' : 'text-gray-500'}>
                  {a.reflection}
                </span>
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Insights — comprensión, no estadística */}
      {quality.insights && quality.insights.length > 0 && (
        <div className="border-t border-gray-100 pt-5" data-testid="quality-insights">
          <p className="text-[11px] uppercase tracking-widest text-habita-dark/70 font-semibold mb-3">
            Observaciones sobre tu proceso
          </p>
          <ul className="space-y-2">
            {quality.insights.map((i) => {
              const isOpen = !!openedInsights[i.id];
              return (
                <li
                  key={i.id}
                  className="rounded-xl bg-habita-mint/30 border border-habita/20 p-3"
                  data-testid={`quality-insight-${i.kind}`}
                >
                  <button
                    onClick={() => toggleInsight(i)}
                    className="w-full text-left flex items-start justify-between gap-3"
                    data-testid={`quality-insight-toggle-${i.kind}`}
                  >
                    <span className="text-sm text-habita-dark font-medium leading-snug">
                      {i.headline}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-habita-dark/60 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="mt-2 text-xs text-gray-600 leading-relaxed" data-testid={`quality-insight-detail-${i.kind}`}>
                      {i.detail && <p className="mb-1">{i.detail}</p>}
                      {i.evidence_refs?.length > 0 && (
                        <p className="text-[10px] uppercase tracking-wide text-habita-dark/50 mt-2">
                          Evidencia: {i.evidence_refs.join(' · ')}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
